import React, {
  ComponentPropsWithoutRef,
  ElementType,
  FC,
  forwardRef,
} from "react";
import { Box, System } from "flicket-ui";
import styled from "styled-components";

import reqIcon from "./req";

const StyledIcon = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  svg {
    vertical-align: middle;
  }
`;

export type IconName =
  | "accessibility_purple"
  | "accessibility"
  | "account"
  | "admin-panel"
  | "afterpay"
  | "afterpay-icon"
  | "alcohol-free"
  | "arrow-dropdown"
  | "assignment"
  | "backspace"
  | "calendar"
  | "calendar-today"
  | "check-circle"
  | "check-circle-thin"
  | "check"
  | "chevron-down"
  | "chevron-left"
  | "chevron-right"
  | "close"
  | "compare"
  | "cursor"
  | "date-range"
  | "dollar"
  | "double-chevron-left"
  | "double-chevron-right"
  | "email"
  | "error"
  | "error-circle-red"
  | "event"
  | "facebook"
  | "facebook-event"
  | "fb-like"
  | "filter"
  | "google"
  | "help"
  | "info"
  | "instagram"
  | "laybuy"
  | "laybuy_sunglasses"
  | "laybuy_lb_heart"
  | "laybuy_cursor"
  | "laybuy_calendar"
  | "logout"
  | "map"
  | "membership"
  | "menu"
  | "messenger"
  | "minus"
  | "more"
  | "my-account"
  | "my-tickets"
  | "print"
  | "plus"
  | "r18"
  | "radio-checked"
  | "radio"
  | "remove-circle"
  | "search"
  | "share"
  | "shopping-cart"
  | "sunglasses"
  | "support"
  | "tick"
  | "ticket"
  | "tiktok"
  | "twitter"
  | "twitter-email"
  | "user-account"
  | "zoom-in"
  | "person-arms-spread"
  | "map-pin"
  | "calendar-blank"
  | "circle-ticket";

type PolymorphicProps<T extends ElementType, Extra = object> = Extra &
  Omit<ComponentPropsWithoutRef<T>, keyof Extra> & {
    as?: T;
  };

export type IconProps<T extends ElementType = "div"> = PolymorphicProps<
  T,
  {
    icon: IconName | JSX.Element;
    as?: string;
    href?: string;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLElement>;
    type?: "button" | "submit";
  } & System
>;

export const Icon = forwardRef(
  <T extends ElementType = "div">(
    { icon, color, ...props }: IconProps<T>,
    ref: React.Ref<any>
  ) => {
    function getAs(): any {
      switch (true) {
        case props.as !== undefined:
          return props.as;
        case props.href !== undefined:
          return "a";
        case props.onClick !== undefined:
          return "button";
        default:
          return "div";
      }
    }

    function getIcon(): JSX.Element {
      if (isIconName(icon)) {
        const IconSvg = reqIcon(`./${icon}.svg`).default as React.FC<
          React.SVGProps<SVGSVGElement>
        >;
        return <IconSvg />;
      }

      return icon;
    }
    return (
      <StyledIcon
        className="icon"
        ref={ref}
        aria-hidden
        as={getAs()}
        color={color}
        {...props}
      >
        {getIcon()}
      </StyledIcon>
    );
  }
);

function isIconName(icon: IconProps["icon"]): icon is IconName {
  return typeof icon === "string";
}

Icon.displayName = "Icon";

export default Icon;
